import {useEffect, useId, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {ChevronLeft, Loader2, Search, Users} from 'lucide-react';
import {useTeamMembers} from '../../../pages/Team/useTeamMembers';
import {fullName, type TeamMember} from '../../../pages/Team/teamFilters';
import {resolveMatrixUserIds} from '../../groupCalls/services/ringClient';
import {CallAvatar} from '../components/CallAvatar';
import {presenceKey} from '../group/teamPresence';
import {groupSurfaceMessages as gs} from '../group/groupMessages';
import {matrixSurfaceMessages as m} from './matrixMessages';

const LOOKUP_BATCH = 100;

interface EscalationPickerProps {
    readonly peerId?: string;
    readonly peerName: string;
    readonly localUserId: string | null;
    readonly busy: boolean;
    readonly failedName?: string;
    readonly onPick: (member: TeamMember) => void;
    readonly onBack: () => void;
    readonly compact?: boolean;
    readonly withHeader?: boolean;
}

export const EscalationPicker = ({peerId, peerName, localUserId, busy, failedName, onPick, onBack, compact = false, withHeader = true}: EscalationPickerProps) => {
    const intl = useIntl();
    const searchId = useId();
    const {members, isLoading} = useTeamMembers();
    const [query, setQuery] = useState('');
    const [matrixIds, setMatrixIds] = useState<ReadonlyMap<string, string>>();
    const [picked, setPicked] = useState<string>();

    useEffect(() => {
        const ids = members.map(member => Number(member.userId)).filter(Number.isFinite);
        if (ids.length === 0) {
            return;
        }
        let cancelled = false;
        const batches = Array.from({length: Math.ceil(ids.length / LOOKUP_BATCH)}, (_, index) => ids.slice(index * LOOKUP_BATCH, (index + 1) * LOOKUP_BATCH));
        Promise.all(batches.map(batch => resolveMatrixUserIds(batch)))
            .then(results => {
                if (!cancelled) {
                    setMatrixIds(new Map(results.flatMap(result => [...result.entries()].map(([userId, matrixUserId]) => [String(userId), matrixUserId] as const))));
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setMatrixIds(new Map());
                }
            });
        return () => {
            cancelled = true;
        };
    }, [members]);

    useEffect(() => {
        if (!busy) {
            setPicked(undefined);
        }
    }, [busy]);

    const visible = useMemo(() => {
        const needle = query.trim().toLowerCase();
        return members
            .filter(member => {
                const matrixUserId = matrixIds?.get(String(member.userId));
                return !matrixUserId || (matrixUserId !== peerId && matrixUserId !== localUserId);
            })
            .filter(member => !needle || fullName(member).toLowerCase().includes(needle) || member.email.toLowerCase().includes(needle))
            .sort((left, right) => fullName(left).localeCompare(fullName(right)));
    }, [localUserId, matrixIds, members, peerId, query]);

    const loading = isLoading || !matrixIds;

    return (
        <div className="flex min-h-0 flex-1 flex-col gap-2" data-testid="one-to-one-add-people">
            {withHeader && <header className="flex items-center gap-2">
                <button
                    type="button"
                    aria-label={intl.formatMessage(m.backToCall)}
                    onClick={onBack}
                    className="phq-call-press inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[var(--phq-call-ink-muted)] hover:bg-[var(--phq-call-surface-muted)]"
                >
                    <ChevronLeft size={18} aria-hidden="true"/>
                </button>
                <h2 className="text-base font-semibold">{intl.formatMessage(m.addTitle)}</h2>
            </header>}
            <p className="text-[13px] leading-[1.4] text-[var(--phq-call-ink-muted)]">{intl.formatMessage(m.addHint, {name: peerName})}</p>
            {failedName && (
                <p role="alert" className="rounded-xl bg-[var(--phq-call-danger-note)] px-3 py-2 text-[13px] text-[var(--phq-call-danger-soft)]">
                    {intl.formatMessage(m.addFailed, {name: failedName})}
                </p>
            )}
            <label htmlFor={searchId} className="flex h-10 items-center gap-2 rounded-[10px] bg-[var(--phq-call-surface-muted)] px-3">
                <Search size={16} className="shrink-0 text-[var(--phq-call-ink-muted)]" aria-hidden="true"/>
                <input
                    id={searchId}
                    type="search"
                    value={query}
                    onChange={event => setQuery(event.target.value)}
                    placeholder={intl.formatMessage(gs.addSearch)}
                    aria-label={intl.formatMessage(gs.addSearch)}
                    className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-[var(--phq-call-ink-muted)]"
                />
            </label>
            {loading && (
                <ul className="flex flex-col gap-2 py-1" aria-busy="true">
                    {[0, 1, 2].map(index => <li key={index} className="h-10 animate-pulse rounded-lg bg-[var(--phq-call-surface-muted)]"/>)}
                </ul>
            )}
            {!loading && visible.length === 0 && (
                <div className="flex flex-col items-center gap-2 py-6 text-center">
                    <Users size={24} className="text-[var(--phq-call-ink-muted)]" aria-hidden="true"/>
                    <p className="text-sm font-semibold">{intl.formatMessage(gs.addEmptyTitle, {query: query.trim()})}</p>
                    <p className="text-xs text-[var(--phq-call-ink-muted)]">{intl.formatMessage(gs.addEmptyBody)}</p>
                </div>
            )}
            {!loading && visible.length > 0 && (
                <ul className={`flex flex-col overflow-y-auto ${compact ? 'max-h-[280px]' : 'max-h-[420px]'}`}>
                    {visible.map(member => {
                        const name = fullName(member);
                        const key = String(member.userId);
                        const adding = busy && picked === key;
                        return (
                            <li key={key} className="flex h-12 items-center gap-3" data-testid="one-to-one-add-row">
                                <CallAvatar identity={{name, avatarUrl: member.avatarUrl || undefined, userId: key}} size={32}/>
                                <div className="min-w-0 flex-1">
                                    <p className="truncate text-sm font-medium">{name}</p>
                                    <p className="truncate text-xs text-[var(--phq-call-ink-muted)]">{intl.formatMessage(gs[presenceKey(member)])}</p>
                                </div>
                                <button
                                    type="button"
                                    data-testid="one-to-one-add-person"
                                    disabled={busy}
                                    aria-label={intl.formatMessage(gs.addActionAria, {name})}
                                    onClick={() => {
                                        setPicked(key);
                                        onPick(member);
                                    }}
                                    className="phq-call-press inline-flex h-7 shrink-0 items-center gap-1.5 rounded-lg bg-[var(--phq-call-brand)] px-3 text-xs font-semibold text-white disabled:opacity-40"
                                >
                                    {adding && <Loader2 size={12} className="animate-spin" aria-hidden="true"/>}
                                    {intl.formatMessage(gs.addAction)}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};
